import {
  DeviceDropdownInputBare,
  EquipmentDropdownInputBare,
  FacilityDropdownInputBare,
  FlowDropdownInputBare,
  FormCategoryDropdownInputBare,
  FormSchemaDropdownInputBare,
  MeasurementTypeDropdownInputBare,
} from "#src/batteries-included-components/Dropdowns";
import {
  FilterBuilderCustomRenderInputProps,
  FilterBuilderFieldsType,
} from "@validereinc/common-components";
import {
  DefaultRecordValueConfigurationFunction,
  DefaultRecordValueConfigurationFunctionNames,
  FormSubmissionStatus,
} from "@validereinc/domain";
import React from "react";
export type FilterEntryType = { id: string; key: string; value: string };

const aggregateFunctionsList = [
  DefaultRecordValueConfigurationFunction.TOP,
  DefaultRecordValueConfigurationFunction.AVG,
  DefaultRecordValueConfigurationFunction.MIN,
  DefaultRecordValueConfigurationFunction.MAX,
  DefaultRecordValueConfigurationFunction.SUM,
  DefaultRecordValueConfigurationFunction.TIME_WEIGHTED_AVG,
];

/** Options for the aggregate function dropdown */
export const AGGREGATE_FUNCTION_OPTIONS = aggregateFunctionsList.map(
  (aggregateFn) => ({
    value: aggregateFn,
    label: DefaultRecordValueConfigurationFunctionNames[aggregateFn],
  })
);

/** Sort options available per configuration type */
export const SORT_OPTIONS_BY_CONFIGURATION_TYPE: Record<
  string,
  Array<{ label: string; value: string }>
> = {
  form_measurement: [
    { label: "Measurement Time", value: "measurement.time" },
    { label: "Measurement Value", value: "measurement.value" },
    {
      label: "Measurement Question Index",
      value: "measurement.question_index",
    },
    { label: "Measurement Creation Time", value: "measurement.created_at" },
    {
      label: "Form Submission Creation Time",
      value: "form_submission.created_at",
    },
    {
      label: "Form Submission Last Edited",
      value: "form_submission.updated_at",
    },
    { label: "Form Template Name", value: "form_schema.name" },
    { label: "Form Template Creation Time", value: "form_schema.created_at" },
    { label: "Form Template Last Edited", value: "form_schema.updated_at" },
    { label: "Form Category Name", value: "form_category.name" },
    { label: "Form Category Creation Time", value: "form_category.created_at" },
    { label: "Form Category Last Edited", value: "form_category.updated_at" },
  ],
  calculator_result: [
    { label: "Year Month", value: "year_month" },
    { label: "Creation Time", value: "created_at" },
    { label: "Measurement Value", value: "measurement_value" },
    { label: "Unique Identifier", value: "id" },
  ],
  record: [
    { label: "Year Month", value: "year_month" },
    { label: "Creation Time", value: "created_at" },
    { label: "Measurement Value", value: "measurement_value" },
    { label: "Unique Identifier", value: "id" },
  ],
};

/** Filterable fields available per configuration type for the FilterBuilder */
export const FILTER_FIELDS_BY_CONFIGURATION_TYPE: Record<
  string,
  FilterBuilderFieldsType
> = {
  form_measurement: {
    id: { data_type: "string", display_name: "Form Submission ID" },
    status: {
      data_type: "pick-list",
      display_name: "Form Submission Status",
      pick_list_options: Object.values(FormSubmissionStatus),
    },
    updated_at: {
      data_type: "date",
      display_name: "Form Submission Last Edited",
      format: "yyyy-MM-dd",
    },
    created_at: {
      data_type: "date",
      display_name: "Form Submission Creation Time",
      format: "yyyy-MM-dd",
    },
    version: {
      data_type: "number",
      display_name: "Form Submission Version",
    },
    "form_schema.version": {
      data_type: "number",
      display_name: "Form Template Version",
    },
    "form_category.name": {
      data_type: "string",
      display_name: "Form Category Name",
    },
    "measurement.source_id": {
      data_type: "string",
      display_name: "Measurement Source ID",
    },
    "measurement.subject_id": {
      data_type: "string",
      display_name: "Measurement Subject ID",
    },
    "measurement.form_submission_id": {
      data_type: "string",
      display_name: "Measurement Form Submission ID",
    },
    "measurement.form_submission_version": {
      data_type: "number",
      display_name: "Measurement Form Submission Version",
    },
    "measurement.section_id": {
      data_type: "string",
      display_name: "Measurement Section ID",
    },
    "measurement.question_id": {
      data_type: "string",
      display_name: "Measurement Question ID",
    },
    "measurement.question_index": {
      data_type: "number",
      display_name: "Measurement Question Index",
    },
    "measurement.time": {
      data_type: "date",
      display_name: "Measurement Time",
      format: "yyyy-MM-dd",
    },
    "measurement.measurement_type": {
      data_type: "custom-pick-list",
      display_name: "Measurement Type",
      renderInput: (props: FilterBuilderCustomRenderInputProps) => (
        <MeasurementTypeDropdownInputBare
          isInline
          isLabelShown={false}
          isMulti={props.isMulti}
          isDisabled={props.isDisabled}
          value={props.value}
          onChange={props.onChange}
        />
      ),
    },
    "measurement.measurement_unit": {
      data_type: "string",
      display_name: "Measurement Unit",
    },
    "measurement.value": {
      data_type: "number",
      display_name: "Measurement Value",
    },
    "form_schema.id": {
      data_type: "custom-pick-list",
      display_name: "Form Template",
      renderInput: (props: FilterBuilderCustomRenderInputProps) => (
        <FormSchemaDropdownInputBare
          isInline
          isLabelShown={false}
          isMulti={props.isMulti}
          isDisabled={props.isDisabled}
          value={props.value}
          onChange={props.onChange}
        />
      ),
    },
    "form_category.id": {
      data_type: "custom-pick-list",
      display_name: "Form Category",
      renderInput: (props: FilterBuilderCustomRenderInputProps) => (
        <FormCategoryDropdownInputBare
          isInline
          isLabelShown={false}
          isMulti={props.isMulti}
          isDisabled={props.isDisabled}
          value={props.value}
          onChange={props.onChange}
        />
      ),
    },
  },
  calculator_result: {
    "estimation_method.id": {
      data_type: "string",
      display_name: "Calculator Result ID",
    },
  },
  measurement_series: {
    id: { data_type: "string", display_name: "Measurement Series ID" },
    device_id: {
      data_type: "custom-pick-list" as const,
      display_name: "Device",
      renderInput: (props: FilterBuilderCustomRenderInputProps) => (
        <DeviceDropdownInputBare
          isInline
          isLabelShown={false}
          isMulti={props.isMulti}
          isDisabled={props.isDisabled}
          value={props.value}
          onChange={props.onChange}
        />
      ),
    },
    measurement_type: {
      data_type: "custom-pick-list",
      display_name: "Measurement Type",
      renderInput: (props: FilterBuilderCustomRenderInputProps) => (
        <MeasurementTypeDropdownInputBare
          isInline
          isLabelShown={false}
          isMulti={props.isMulti}
          isDisabled={props.isDisabled}
          value={props.value}
          onChange={props.onChange}
        />
      ),
    },
    interval: {
      data_type: "pick-list",
      display_name: "Interval",
      pick_list_options: ["1day", "1hour", "15minute"],
      pick_list_labels: {
        "1day": "1 Day",
        "1hour": "1 Hour",
        "15minute": "15 Minute",
      },
    },
    func: {
      data_type: "pick-list",
      display_name: "Function",
      pick_list_options: ["avg", "sum"],
      pick_list_labels: {
        avg: "Average",
        sum: "Sum",
      },
    },
    start: {
      data_type: "date",
      display_name: "Start Date",
      format: "yyyy-MM-dd",
    },
    end: {
      data_type: "date",
      display_name: "End Date",
      format: "yyyy-MM-dd",
    },
  },
  record: {
    "flow.id": {
      data_type: "custom-pick-list",
      display_name: "Flow",
      renderInput: (props: FilterBuilderCustomRenderInputProps) => (
        <FlowDropdownInputBare
          isInline
          isLabelShown={false}
          isMulti={props.isMulti}
          isDisabled={props.isDisabled}
          value={props.value}
          onChange={props.onChange}
        />
      ),
    },
    "equipment.id": {
      data_type: "custom-pick-list",
      display_name: "Equipment",
      renderInput: (props: FilterBuilderCustomRenderInputProps) => (
        <EquipmentDropdownInputBare
          isInline
          isLabelShown={false}
          isMulti={props.isMulti}
          isDisabled={props.isDisabled}
          value={props.value}
          onChange={props.onChange}
        />
      ),
    },
    "facility.id": {
      data_type: "custom-pick-list",
      display_name: "Facility",
      renderInput: (props: FilterBuilderCustomRenderInputProps) => (
        <FacilityDropdownInputBare
          isInline
          isLabelShown={false}
          isMulti={props.isMulti}
          isDisabled={props.isDisabled}
          value={props.value}
          onChange={props.onChange}
        />
      ),
    },
    measurement_type: {
      data_type: "custom-pick-list",
      display_name: "Measurement Type",
      renderInput: (props: FilterBuilderCustomRenderInputProps) => (
        <MeasurementTypeDropdownInputBare
          isInline
          isLabelShown={false}
          isMulti={props.isMulti}
          isDisabled={props.isDisabled}
          value={props.value}
          onChange={props.onChange}
        />
      ),
    },
  },
};
